/**
 * The Help › Abbreviations list, served by GET /reference/abbreviations behind `help.view`.
 *
 * Short forms used on arrival forms and printed notes, from the legacy WMS page "Arrival › Utility ›
 * Abbreviations", and the Stores short forms from seedtrac STORES' "Utility › Abbreviations" (2026-10-07). One row per abbreviation: OP, printed twice there, carries both of its meanings here.
 * Obvious misspellings in the legacy list are corrected (Delivary, Pernmant, Ware House).
 */
export interface Abbreviation {
  abbr: string;
  meanings: string[];
  /** A badge: the legacy list's "(Seed Status)" suffix, or a short form only the Stores module uses. */
  tag?: "Seed status" | "Stores";
}

const a = (
  abbr: string,
  meaning: string | string[],
  tag?: "Seed status" | "Stores"
): Abbreviation => ({
  abbr,
  meanings: Array.isArray(meaning) ? meaning : [meaning],
  ...(tag && { tag }),
});

export const ABBREVIATIONS: Abbreviation[] = [
  a("#", "Serial Number"),
  a("Acc", "Acceptable"),
  a("Admin", "Administrator"),
  a("AF", "Arrival Fresh"),
  a("AR1", "Arrival Operator 1"),
  a("AS", "Arrival Stock Transfer"),
  a("AT", "Arrival Trading"),
  a("AU", "Arrival Unidentified"),
  a("BLD", "Blend Lots (cob blend at a dryer)"),
  a("CAR", "COB Arrival"),
  a("CCN", "Captive Consumption Note", "Stores"),
  a("CDS", "Load Truck to Plant (a dryer's load)"),
  a("CI", "Cycle Inventory", "Stores"),
  a("CST", "Central Sales Tax"),
  a("D", "Drying", "Seed status"),
  a("D.C./Inv. No.", "Delivery Challan Invoice Number", "Stores"),
  a("D2GN", "Damage To Good Note", "Stores"),
  a("DC", "Delivery Challan"),
  a("DC No.", "Delivery Challan Number", "Stores"),
  a("DC Ref.", "Delivery Challan Reference", "Stores"),
  a("DOGR", "Date of GOT Result"),
  a("DoGT", "Date of GOT Test"),
  a("DOS", "Date of Sowing"),
  a("DOSC", "Date of Sample Collect"),
  a("DOSD", "Date of Sample Dispatch"),
  a("DOSR", "Date of Sample Request"),
  a("DoT", "Date of QC Test"),
  a("DRY", "Drying & Shelling (cob drying run)"),
  a("DtoG", "Damage To Good", "Stores"),
  a("e-Indent", "Electronic Indent", "Stores"),
  a("E-mail", "Electronic Mail"),
  a("EDOR", "Expected Date of Result"),
  a("ESN", "Excess Shortage Note", "Stores"),
  a("EX", "Expired (QC validity has run out)"),
  a("Ex/Sh.", "Excess Shortage", "Stores"),
  a("F", "Fumigation", "Seed status"),
  a("FAQ", "Frequently Asked Question"),
  a("FRN", "Fresh Seed Receipt Note"),
  a("G2DN", "Good To Damage Note", "Stores"),
  a("Germ%", "Germination Percentage"),
  a("GH", "Guard Warehouse"),
  a("GI", "Geographical Index"),
  a("Gms.", "Grams"),
  a("GOT", "Grow Out Test"),
  a("GOT-NR", "Grow Out Test – Not Recommended"),
  a("GOT-R", "Grow Out Test – Recommended"),
  a("GRN", "Good Receive Note", "Stores"),
  a("GRS", "GGOT Record Sheet"),
  a("GRS No.", "GGOT Record Sheet Number"),
  a("GS", "Guard Sample"),
  a("GSDN", "Gate Sample Dispatch Note"),
  a("GSRP", "Guard Sample Retention Period"),
  a("GtoD", "Good To Damage", "Stores"),
  a("ID", "Identification"),
  a("IITN", "Inter Item Transfer Note", "Stores"),
  a("IMRN", "Internal Material Return Note", "Stores"),
  a("IMRN-O", "Internal Material Return Note – Own", "Stores"),
  a("IMRN-P", "Internal Material Return Note – Party", "Stores"),
  a("Kg", "Kilogram"),
  a("Kgs.", "Kilograms"),
  a("MDN", "Material Discard Note", "Stores"),
  a("MDSRN", "Maize Dried Seed Receipt Note"),
  a("Moist%", "Moisture Percentage"),
  a("No.", "Number"),
  a("NoB", "Number of Bags"),
  a("nos.", "Numbers", "Stores"),
  a("NoWB", "Number of Window Box"),
  a("NUT", "Not Under Test (GOT not required)"),
  a("OLNL", "Old Lot to New Lot Note"),
  a("OP", ["Open Pollination", "Opening Stock"]),
  a("P-Indent", "Physical Indent", "Stores"),
  a("PAN", "Permanent Account Number"),
  a("PDN", "Production Dispatch Number"),
  a("PHSRN", "Post Harvest Seed Receipt Note"),
  a("PIN", "Physical Indent Note", "Stores"),
  a("PM", "Plant Manager"),
  a("PO", "Purchase Order"),
  a("PP", "Physical Purity"),
  a("PR", "Pending Review (QC)"),
  a("Prod. Grade", "Production Grade"),
  a("Prod. Loc.", "Production Location"),
  a("Prod. Pers.", "Production Personnel"),
  a("Q", "Quarantine", "Seed status"),
  a("QC", "Quality Control"),
  a("QG", "Quality GOT (Transaction Code)"),
  a("QN", "Quarantined (QC)"),
  a("Qty", "Quantity"),
  a("R", "Reserve", "Seed status"),
  a("Ref.", "Reference"),
  a("Rej", "Rejected (not acceptable)"),
  a("RJ", "Rejected (QC)"),
  a("RL", "Released (QC Soft Release)"),
  a("RSW", "Raw Seed Warehouse"),
  a("RT", "Retest"),
  a("Seed Lic. No.", "Seed License Number"),
  a("SIG%", "Significant Indicative Germination Percentage"),
  a("SLOC", "Storage Location"),
  a("SP", "Seed Production"),
  a("SPC-F", "Seed Production Code – Female"),
  a("SPC-M", "Seed Production Code – Male"),
  a("SRV", "Stores Report Viewer", "Stores"),
  a("STIN", "Stock Transfer Issue Note", "Stores"),
  a("STN No.", "Stock Transfer Note Number", "Stores"),
  a("STON", "Stock Transfer Out Note", "Stores"),
  a("STR Date", "Stock Transfer Request Date", "Stores"),
  a("STR Ref. No.", "Stock Transfer Reference Number", "Stores"),
  a("STRN", "Stock Transfer Receipt Note", "Stores"),
  a("T", "Temporary (before the transaction ID)"),
  a("TBB", "To Be Billed", "Stores"),
  a("TDF", "Trial / Demo / Free"),
  a("TIN", "Tax Identification Number"),
  a("TIP", "Test in Progress"),
  a("TRN-I", "Trading Seed Receipt Note – Internal"),
  a("TRN-P", "Trading Seed Receipt Note – Party"),
  a("UAN", "Unidentified Arrival Note"),
  a("UoM", "Unit of Measurement", "Stores"),
  a("UPS", "Unit Pack Size", "Stores"),
  a("UT", "Under Test"),
  a("V. Lot No.", "Vendor Lot Number"),
  a("VMRN", "Vendor Material Return Note", "Stores"),
  a("WB", "Window Box"),
  a("WH", "Warehouse"),
  a("Wt", "Weight"),
];
